import React, { useState } from "react";
import { useForm } from "../../hooks/useForm";

const PersonnelForm = ({
  initialValues,
  onSubmit,
  onCancel,
  title,
  submitText,
  isEditing = false,
  existingUnits = [],
}) => {
  // Validation rules
  const validationRules = {
    personalNumber: (value) => {
      if (!value?.trim()) return "מספר אישי נדרש";
      if (!/^\d+$/.test(value)) return "מספר אישי חייב להכיל רק ספרות";
      return "";
    },
    firstName: (value) => {
      if (!value?.trim()) return "שם פרטי נדרש";
      return "";
    },
    lastName: (value) => {
      if (!value?.trim()) return "שם משפחה נדרש";
      return "";
    },
    phone: (value) => {
      if (!value?.trim()) return "טלפון נדרש";
      if (!/^[\d\-\+\(\)\s]+$/.test(value)) return "פורמט טלפון לא תקין";
      return "";
    },
    rank: (value) => {
      if (!value?.trim()) return "דרגה נדרשת";
      return "";
    },
    unitId: (value) => {
      if (!value || value === "") return "יחידה נדרשת";
      const unitExists = existingUnits.some((unit) => unit.unitId === value);
      if (!unitExists) return "יחידה לא קיימת";
      return "";
    },
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    resetForm,
    setFieldValue,
    setFieldError,
  } = useForm(initialValues, validationRules);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit(values);
    }
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  return (
    <form onSubmit={handleSubmit} className="personnel-form">
      <div className="form-group">
        <label htmlFor="personalNumber">מספר אישי:</label>
        <input
          type="text"
          id="personalNumber"
          name="personalNumber"
          value={values.personalNumber}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          className={
            touched.personalNumber && errors.personalNumber ? "error-input" : ""
          }
          placeholder="הזינו מספר אישי"
        />
        {touched.personalNumber && errors.personalNumber && (
          <div className="error-message">{errors.personalNumber}</div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="firstName">שם פרטי:</label>
        <input
          type="text"
          id="firstName"
          name="firstName"
          value={values.firstName}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          className={touched.firstName && errors.firstName ? "error-input" : ""}
          placeholder="הזינו שם פרטי"
        />
        {touched.firstName && errors.firstName && (
          <div className="error-message">{errors.firstName}</div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="lastName">שם משפחה:</label>
        <input
          type="text"
          id="lastName"
          name="lastName"
          value={values.lastName}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          className={touched.lastName && errors.lastName ? "error-input" : ""}
          placeholder="הזינו שם משפחה"
        />
        {touched.lastName && errors.lastName && (
          <div className="error-message">{errors.lastName}</div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="nickname">כינוי (אופציונלי):</label>
        <input
          type="text"
          id="nickname"
          name="nickname"
          value={values.nickname ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          className={touched.nickname && errors.nickname ? "error-input" : ""}
          placeholder="הזינו כינוי אם יש"
        />
        {touched.nickname && errors.nickname && (
          <div className="error-message">{errors.nickname}</div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="phone">טלפון:</label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={values.phone}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          className={touched.phone && errors.phone ? "error-input" : ""}
          placeholder="הזינו מספר טלפון"
        />
        {touched.phone && errors.phone && (
          <div className="error-message">{errors.phone}</div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="rank">דרגה:</label>
        <select
          id="rank"
          name="rank"
          value={values.rank}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          className={touched.rank && errors.rank ? "error-input" : ""}
        >
          <option value="">בחר דרגה</option>
          <option value="טוראי">טוראי</option>
          <option value="רב טוראי">רב טוראי</option>
          <option value="סמל">סמל</option>
          <option value="סמל ראשון">סמל ראשון</option>
          <option value="רב סמל">רב סמל</option>
          <option value="רב סמל ראשון">רב סמל ראשון</option>
          <option value="רב סמל מתקדם">רב סמל מתקדם</option>
          <option value="רב סמל בכיר">רב סמל בכיר</option>
          <option value="סגן">סגן</option>
          <option value="סרן">סרן</option>
          <option value="רב סרן">רב סרן</option>
          <option value="סגן אלוף">סגן אלוף</option>
          <option value="אלוף משנה">אלוף משנה</option>
          <option value="תת אלוף">תת אלוף</option>
          <option value="אלוף">אלוף</option>
          <option value="רב אלוף">רב אלוף</option>
        </select>
        {touched.rank && errors.rank && (
          <div className="error-message">{errors.rank}</div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="unitId">יחידה:</label>
        <select
          id="unitId"
          name="unitId"
          value={values.unitId || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          className={touched.unitId && errors.unitId ? "error-input" : ""}
        >
          <option value="">בחר יחידה</option>
          {existingUnits.map((unit) => (
            <option key={unit.unitId} value={unit.unitId}>
              {unit.unitName}
            </option>
          ))}
        </select>
        {touched.unitId && errors.unitId && (
          <div className="error-message">{errors.unitId}</div>
        )}
      </div>

      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="isAttached"
            checked={values.isAttached || false}
            onChange={(e) => setFieldValue("isAttached", e.target.checked)}
          />
          <span className="checkbox-text">מסופח</span>
        </label>
      </div>

      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="isSecret"
            checked={values.isSecret || false}
            onChange={(e) => setFieldValue("isSecret", e.target.checked)}
          />
          <span className="checkbox-text">משקש</span>
        </label>
      </div>

      <div className="form-actions">
        <button type="submit" className="save-btn">
          {submitText}
        </button>
        <button type="button" className="cancel-btn" onClick={handleCancel}>
          ביטול
        </button>
      </div>
    </form>
  );
};

export default PersonnelForm;
